import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { PatientCardCommunicationService } from '@modules/patient-card/services/patient-card-communication.service';
import { PatientAppIframeCommunicationService } from '@modules/patient/services/patient-app-iframe-communication.service';
import { PatientDisplayMode } from '@modules/patient/models/patient-display-mode.enum';
import { PatientDisplayModeService } from '@modules/patient/services/patient-display-mode.service';
import { PatientAppLoadStateService } from '@modules/rx-for-doctor/services/patient-app-load-state.service';
import { UnifiedPatientsCommunicationService } from '@shared/services/unified-patients-communication.service';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

/**
 * Mode-agnostic adapter for patient save/clear operations.
 *
 * Routes to the correct communication service based on the current display mode:
 * - PatientCard (MFE) → PatientCardCommunicationService (primary path)
 * - PatientApp (iframe) → PatientAppIframeCommunicationService (fallback)
 * - UnifiedPatients (XState) → UnifiedPatientsCommunicationService (transitional, to be removed)
 *
 * Consumers call a single method; the adapter resolves the transport internally.
 * Validation is NOT handled here — it stays in the consumers.
 */
@Injectable({ providedIn: 'root' })
export class PatientAppAdapter {
	private readonly moduleName = 'PatientAppAdapter';

	readonly isPatientAppReady$ = this.patientAppLoadStateService.isPatientReadyAndLoaded$;

	constructor(
		private readonly patientDisplayModeService: PatientDisplayModeService,
		private readonly patientCardCommunicationService: PatientCardCommunicationService,
		private readonly patientAppIframeCommunicationService: PatientAppIframeCommunicationService,
		private readonly unifiedPatientsCommunicationService: UnifiedPatientsCommunicationService,
		private readonly patientAppLoadStateService: PatientAppLoadStateService,
		private readonly logger: LoggerService
	) {}

	savePatient(): Observable<boolean> {
		return this.patientDisplayModeService.displayMode$.pipe(
			take(1),
			switchMap(mode => {
				switch (mode) {
					case PatientDisplayMode.PatientCard:
						this.logger.info('Saving patient via PatientCard MFE', { module: this.moduleName });

						return this.patientCardCommunicationService.savePatient();

					case PatientDisplayMode.UnifiedPatients:
						this.logger.info('Saving patient via UnifiedPatients', { module: this.moduleName });

						return this.unifiedPatientsCommunicationService.savePatient();

					case PatientDisplayMode.PatientApp:
						this.logger.info('Saving patient via legacy iframe', { module: this.moduleName });

						return this.patientAppIframeCommunicationService.savePatient();

					default:
						this.logger.error(`Unknown display mode: ${mode}`, { module: this.moduleName });

						return of(false);
				}
			})
		);
	}

	clearPatient(): void {
		this.patientDisplayModeService.displayMode$.pipe(take(1)).subscribe(mode => {
			switch (mode) {
				case PatientDisplayMode.PatientCard:
					this.patientCardCommunicationService.clearPatient();
					break;

				case PatientDisplayMode.PatientApp:
					this.patientAppIframeCommunicationService.postMessageForClearPatient();
					break;

				case PatientDisplayMode.UnifiedPatients:
					// Machine handles clear internally via CARD.CLEAR_CLICKED event — no-op here
					break;
			}
		});
	}
}
